<template>
    <div class="">
        <div id="exportable-content">
            <Hero title="Privacy Policy" colour-index="0" subtitle="Date of Last Revision: October 27, 2026" :left="true" />

            <div class="privacy px-3 sm:px-28 sm:mt-12 sm:pb-12 text-[var(--black)]">
                <h5>Quick guide</h5>
                <p>
                    This Privacy Policy explains how we collect, use, and protect data for individuals who interact directly
                    with our platform, including our customers and their employees, developers, and other users who log into
                    and use the platform.
                </p>
                <p>
                    If you are looking for details on how we handle data processed on behalf of our customers (for example,
                    their end users or customers), please refer instead to our Data Processing Agreement (DPA):
                    <a href="/gdpr/dpa">https://unified.to/gdpr/dpa</a>
                </p>
                <p>
                    Unified.to does not store end-customer data — data belonging to your users or customers that flows
                    through connected integrations is processed in real time and not persisted. We also do not train
                    machine-learning models on any data.
                </p>

                <h5>How we secure your data</h5>
                <p>
                    We use a single-tenant architecture, meaning each customer’s data is logically isolated.
                    All data is encrypted both in transit and at rest. API access keys are scoped to a single workspace and
                    can optionally be restricted further to a specific environment within that workspace.
                    For additional control, customer and developer credentials can be stored in your own Secrets Manager
                    account.
                </p>
                <p>
                    We store platform and account data — such as workspace configuration, integration credentials, and
                    operational logs — in managed databases (document store, columnar analytics store for logs, and an
                    in-memory cache/queue) hosted on AWS cloud infrastructure. We do not store end-customer data at rest.
                    Unified.to operates regional deployments in the United States, European Union, and Australia, and your
                    workspace's data is stored in the region associated with your account so that data residency is
                    preserved; it is not moved between regions except as needed to provide the service or as you configure.
                    Data is encrypted in transit (TLS) and sensitive fields (such as integration secrets) are encrypted at
                    rest.
                </p>



                <h5>Introduction</h5>

                <p>
                    Your privacy is very important to Unified API Inc. ("Unified", "we", "us", or "ours"). This privacy
                    policy ("Privacy Policy") applies to Unified's websites (unified.to, api.unified.to, app.unified.to and
                    any sub-domains) (collectively, the "Websites"), its unified API integration platform (the "Platform"),
                    or any other products or services that link to this Privacy Policy (collectively with the Websites and
                    the Platform, the "Services"). This Privacy Policy describes how we collect, use, disclose, safeguard,
                    or otherwise process personal information of individuals who access or use the Services.
                </p>

                <p>
                    <i>Customer Data</i> — When our customers ("Customers") use our Services, we generally process and store
                    limited personal information on their behalf as a "data processor" or "service provider" as such terms
                    or similar terms are defined under applicable law. For example, in the context of the Platform, when a
                    Customer uses the Platform to connect their own applications and services to their end users'
                    third-party accounts and transfer data between the Platform and those end user accounts, we act as a
                    data processor and process that information on the Customers' behalf and in accordance with their
                    instructions. In those instances, the Customer is the "data controller" and the terms of the agreement
                    with the Customer and the Customer's own privacy policy may govern how we process your personal
                    information. We do not store, persist, or maintain such information transmitted through our APIs, which
                    is processed transiently in order to facilitate real-time transfer between systems and is not retained
                    by us following transmission, except where strictly necessary for security, debugging, or service
                    integrity purposes. This Privacy Policy does not apply to personal information that we process as a
                    data processor on behalf of our Customers.
                </p>

                <p>
                    If you have any questions or concerns about how personal information is processed in these cases,
                    including how to exercise your rights as a data subject, you should contact the Customer directly.
                </p>

                <p>
                    <i>Links to Third-Party Websites</i> — Our Services may contain links to third-party websites and/or
                    applications that are not operated or controlled by us. If you click on a third-party link on our
                    Services, you will be directed to that third-party's website or application. Such websites and
                    applications operate independently from us and have their own privacy policies and/or notices. This
                    Privacy Policy does not apply to any third-party websites and apps that you may use, including those
                    which we link to in our Services. We encourage you to read the terms and policies for each third-party
                    website and app with which you interact. We do not endorse, screen, or approve and assume no
                    responsibility for the privacy policies or practices or content of such other websites or apps.
                    Visiting these other websites or apps is at your own risk.
                </p>

                <p>
                    In addition, our Services may include social media buttons (including plugin or widgets, such as the
                    "share this" button or other interactive mini-programs). Your interactions with these features, apart
                    from your use of our Services, are governed by the privacy policies of the third parties providing the
                    features.
                </p>

                <h5>Personal Information Collected</h5>

                <p>We may collect personal information as follows:</p>

                <h5>Information you give us</h5>

                <p>
                    <i>Registration information</i> — When you register to use our Services, we may collect your name,
                    email address, and similar account information. You may log into your Unified account to access the
                    Services using third-party sign-in functionality such as via your Google or GitHub account. This feature
                    uses those services' application programming interfaces (API) to authenticate your identity and
                    automatically share certain personal information with us like your name and email. We use similar
                    methods to connect your cloud service accounts. Our use of information received from Google APIs,
                    including any and all data we access when end users connect their accounts through Unified's own Google
                    OAuth application, will adhere to
                    <a target="_blank" href="https://developers.google.com/terms/api-services-user-data-policy">API Services
                        User Data Policy</a> including the Limited Use requirements, where applicable.
                </p>

                <p>
                    <i>Third-party connected accounts</i> — All policies that apply to End-User Accounts also apply to any
                    third-party accounts of your own that you connect to the Services.
                </p>

                <p>
                    <i>Payment and Transaction Information</i> — If you choose to use a paid Unified account or Services,
                    our payment-processing vendor, Stripe, directly collects your credit card information and billing
                    address on our behalf and may provide us with certain transaction details including the last four
                    digits of a credit card number and the billing address. Stripe commits to complying with the Payment
                    Card Industry Data Security Standard (PCI-DSS) and using industry-standard security. Stripe may use your
                    Payment Information in accordance with its own Privacy Policy here:
                    <a target="_blank" href="https://stripe.com/us/privacy">https://stripe.com/us/privacy</a>.
                </p>

                <p>
                    <i>Feedback and correspondence</i> — Information you provide when you respond to surveys, report a
                    problem with the Services, receive customer support, let us know how you use the Services, or otherwise
                    communicate with us by email, text, online chat, telephone ("Communications").
                </p>

                <p>
                    <i>Marketing information</i> — Your preferences for receiving marketing communications and details on
                    how you interact with this option to choose your preferences.
                </p>

                <h5>Information automatically collected</h5>

                <p>
                    We, or third parties on our behalf, may collect certain information automatically using various
                    technologies, such as session replay software, cookies, and pixels, when you use the Services and store
                    it in log files. For more information related to our use of tracking technologies, see the
                    <a href="#tracking-technologies">"Tracking Technologies"</a> section below. The information that may be
                    collected automatically includes:
                </p>

                <p>
                    <i>Device Information</i> — Information such as your internet protocol (IP) addresses and approximate
                    location derived from it (i.e. city, region or country), user settings, MAC address, other device
                    identifiers, browser type and version, internet service provider (ISP), or operating system.
                </p>

                <p>
                    <i>Usage Data</i> — Information about how you interact with the Services or our Communications,
                    including referring/exit pages, date/time stamp of access, links clicked on, search terms, frequency and
                    duration of activity, and other statistics.
                </p>

                <h5>Information collected from other sources</h5>

                <p>We may collect your personal information from the following third parties:</p>

                <ul>
                    <li>
                        <i>Data enrichment and sales intelligence providers</i>: business contact and company information
                        (e.g. name, work email, job title, company, company size, LinkedIn profile) used to enrich leads and
                        sign-ups for sales and marketing.
                    </li>
                    <li>
                        <i>Events and marketing lists</i>: contact details from events we attend or sponsor, and from
                        marketing lists we obtain, used to facilitate our sales efforts.
                    </li>
                    <li>
                        <i>Advertising platforms (e.g. LinkedIn, Google Ads)</i>: information about interactions with our
                        ads, such as clicks and conversions, used to measure and improve our marketing.
                    </li>
                </ul>

                <h5>Use of Personal Information</h5>

                <p>We may use your personal information:</p>

                <ul>
                    <li>
                        to operate, provide, maintain, administer, and improve the Services, and communicate with you in the
                        process of doing so to manage your Services account if you have one or inform you of Services
                        announcements, technical notices, updates, and support or administrative messages;
                    </li>
                    <li>
                        to request feedback and to enable us to develop, customize and improve the Services and our
                        publications;
                    </li>
                    <li>
                        to send Communications for marketing, informational, product development and promotional purposes,
                        such as surveys, newsletters, news about our Services, activities, special events or offers from
                        Unified or our partners;
                    </li>
                    <li>to respond to your inquiries and other Communications and offer support services;</li>
                    <li>
                        to perform after-sales services, including anticipating and resolving problems with our Services;
                    </li>
                    <li>
                        to send you notices and other Communications about your use of our Services, including any notices
                        concerning violations of use and updates to the Services;
                    </li>
                    <li>
                        to analyze usage of the Services and Communications, at times through the creation of anonymous
                        aggregate data;
                    </li>
                    <li>
                        to detect, investigate, and prevent violation of and enforce our Terms of Service and secure and
                        protect the integrity of our systems and the Services;
                    </li>
                    <li>to protect either our rights, privacy, safety, or property and/or that of you or others;</li>
                    <li>to detect, investigate and prevent fraudulent, harmful, unauthorized, or illegal activity;</li>
                    <li>
                        to comply with legal obligations, respond to lawful requests from government or public authorities,
                        or to prosecute or defend legal claims; and
                    </li>
                    <li>
                        to share customer stories, case studies, quotes, or other testimonials from you on our Services,
                        with your consent.
                    </li>
                </ul>

                <h5>Transfer of Personal Information</h5>

                <p>We may transfer or make available your personal information under the following circumstances:</p>

                <p>
                    <i>With affiliates</i> — We may transfer or make available your personal information among our corporate
                    affiliates, subsidiaries, and divisions for the purposes described in this Privacy Policy.
                </p>

                <p>
                    <i>With service providers</i> — We may use third-party partners to help us operate, maintain, and
                    deliver our Services. We may also transfer or make available your personal information, including Usage
                    Data, with our service providers and other third-parties that provide products or services for or
                    through our Websites or for our business (such as website or database hosting companies, address list
                    hosting companies, communications providers, email service providers, analytics companies, distribution
                    companies, fulfillment companies, credit card processing companies and other similar service providers
                    that use such information on our behalf).
                </p>

                <p>
                    Third-party service providers are generally contractually restricted from using or disclosing the
                    personal information except as necessary to perform services on our behalf or to comply with legal
                    requirements, or otherwise subject to the terms of this Privacy Policy.
                </p>

                <p>
                    <i>Service Providers</i> — Unified uses certain third-party subprocessors to assist in providing the
                    Services. These subprocessors may process personal data on our behalf in accordance with contractual
                    obligations and applicable data protection laws. A current list of subprocessors is available at:
                    <a href="/gdpr/subprocessors">https://unified.to/gdpr/subprocessors</a>
                </p>

                <p>
                    <i>With your employer</i> — If you are an individual Unified registered user, and the domain of the
                    primary email address associated with your Unified account is owned by your employer and that email
                    address was assigned to you as an employee of that organization, and that organization wishes to
                    establish a Unified corporate account and add you to it, then certain information concerning past use
                    of your individual account may become accessible to that organization's administrator including your
                    email address.
                </p>

                <p>
                    <i>With professional advisors</i> — We may transfer or make available your personal information to
                    professional advisors, such as lawyers, bankers, auditors and insurers, where necessary in the course of
                    the professional services they render to us.
                </p>

                <p>
                    <i>With payment processors</i> — We may transfer your payment information to process your payments as
                    described in the Payment and Transaction Information section above.
                </p>

                <p>
                    <i>As aggregate statistics</i> — We may disclose non-personally identifiable aggregate statistics
                    regarding user behavior as a measure of interest in, and use of, our Websites and Communications to
                    third parties in the form of aggregate data, such as overall patterns or demographic reports, which do
                    not describe or identify any individual user. Information relating to aggregate statistics may be
                    collected through the use of tracking technologies as further described below.
                </p>

                <p>
                    <i>In exigent circumstances</i> — We may disclose or make available your personal information if
                    required to do so by law or legal process or if we believe that such action is necessary to (a) conform
                    to the law, comply with a judicial or court order or comply with legal process served on us or
                    affiliates; (b) protect and defend your, our, or others' rights, property, safety, or other vital
                    interests; (c) enforce our policies or contracts; (d) collect amounts owed to us; or (e) when we
                    believe, in good faith, disclosure is necessary or appropriate to prevent physical harm or financial
                    loss in connection with an investigation or prosecution of suspected or actual illegal activity.
                </p>

                <p>
                    <i>In case of a business transfer</i> — In the event that all or a portion of Unified, our business, or
                    our assets are acquired by or merged with a third-party as part of a business transaction, such as a
                    merger, acquisition, joint venture, financing, corporate reorganization, or purchase or sale of all or a
                    portion of its assets, or in the unlikely event of insolvency, bankruptcy, or receivership, personal
                    information collected by Unified will be shared with or transferred to that third-party as part of such
                    a transaction, including at the negotiation stage, as permitted by law and/or contract.
                </p>

                <h5 id="tracking-technologies">Tracking Technologies</h5>

                <p>
                    Our Services use cookies and similar technologies provided by us and Third Parties to collect your
                    personal information. These technologies include:
                </p>

                <p>
                    <i>Cookies</i> — Cookies are small data files stored on your device that identify your browser. We use
                    session cookies (which expire when you close your browser) and persistent cookies (which remain on your
                    device and support personalization, security, and advertising). Persistent cookies generally may be
                    managed through your browser settings and may transmit data to Third Parties.
                </p>

                <p>
                    <i>Pixels (also known as web beacons)</i> — Pixels are small pieces of code embedded in a website,
                    video, email, or advertisement that send information about your activity to a server. We use pixels, in
                    combination with cookies, to track Communications, bring you advertising both on and off the Websites,
                    and provide you with additional functionality.
                </p>

                <p>
                    <i>App Technologies</i> — Our Services may include software development kits and other
                    non-browser-based technologies that send information about your usage to a server, enabling tracking,
                    advertising, and additional functionality.
                </p>

                <p>
                    <i>Analytics Services</i> — We use various Third Parties, including Google Analytics (at
                    <a target="_blank"
                        href="https://policies.google.com/technologies/partner-sites">https://policies.google.com/technologies/partner-sites</a>),
                    to collect analytics information regarding user behaviors and demographics on our Services and
                    Communications. For more information on how these Third Parties collect, process, and protect your
                    personal information, please see their respective privacy policy.
                </p>

                <p>
                    <i>Session Replay</i> — Our Services may, from time to time, use session replay technology that allows
                    us to record and review how users interact with the Services, including clicks, scrolls, mouse
                    movements, and keystrokes, for internal analytical purposes and to improve our Services.
                </p>

                <p>
                    You can use third-party browser extensions to block cookies you would not prefer, although it may
                    adversely affect your use of the Services.
                </p>

                <h5 id="privacy-choices">Your Privacy Choices</h5>

                <p>
                    If your personal information changes in any way, we invite you to correct or update your information as
                    soon as possible. You can make updates to your profile information by logging into your account on
                    Unified at any time. You can also request changes or access to your information by emailing
                    <a href="mailto:privacy@unified.to">privacy@unified.to</a>.
                </p>

                <p>
                    When you visit or log in to our website, cookies and similar technologies may be used by our online data
                    partners or vendors to associate these activities with other personal information they or others have
                    about you, including by association with your email. We (or service providers on our behalf) may then
                    send communications and marketing to these email addresses. You may opt out of receiving this
                    advertising by visiting
                    <a href="https://app.retention.com/optout" target="_blank" rel="noopener">https://app.retention.com/optout</a>.
                    You also have the option to opt out of the collection of your personal data in compliance with GDPR by
                    visiting
                    <a href="https://www.rb2b.com/rb2b-gdpr-opt-out" target="_blank" rel="noopener">https://www.rb2b.com/rb2b-gdpr-opt-out</a>.
                </p>

                <p>
                    You can also change your cookie preferences on our website at any time, where a cookie banner is shown
                    in your region, by selecting <a href="#privacy-choices" class="cky-banner-element">Cookie Settings</a>.
                </p>

                <p>
                    <i>For individuals in Quebec</i> — If you are based in Quebec, you may ask that we delete your personal
                    information in certain circumstances. You may also ask, in certain circumstances, that we stop sharing
                    your personal information or remove links to your personal information.
                </p>

                <p>
                    If you gave us permission to quote you on our Services' website or otherwise make your personal
                    information, such as your full name, public, but wish to revoke this permission, please contact us at
                    <a href="mailto:privacy@unified.to">privacy@unified.to</a>.
                </p>

                <p>
                    You may opt-out of receiving promotional emails from Unified by following the opt-out instructions
                    provided in those emails. You may also opt-out of receiving promotional emails and other promotional
                    communications from us at any time by emailing privacy@unified.to with your specific request. Opt-out
                    requests will not apply to transactional service messages, such as security alerts and notices about
                    your current account and services. To stop receiving emails you would have to deactivate your account.
                    Please email privacy@unified.to from the email address associated with your Unified account with a
                    request to deactivate your account and we will respond to your request within 5 business days.
                </p>

                <h5>"Do Not Track" and GPC</h5>

                <p>
                    Some browsers or extensions may support privacy preference signals, such as "Do Not Track" or Global
                    Privacy Control ("GPC"). Our Services do not currently change the way they operate based upon detection
                    of a "Do Not Track" or GPC signal, and our privacy practices will continue to apply as described in this
                    Privacy Policy.
                </p>

                <h5>Account and Workspace Data</h5>

                <p>
                    Customers may access, modify, or delete their workspace data at any time through the Platform or by
                    contacting us at <a href="mailto:hello@unified.to">hello@unified.to</a>.
                </p>

                <p>
                    Upon cancellation or termination of a Customer's account, associated workspace data is deleted within 30
                    days, except where retention is required to comply with applicable law, to detect or prevent fraud or
                    security incidents, or to resolve disputes or enforce our agreements.
                </p>

                <p>
                    Customers are responsible for exporting any data they wish to retain prior to account termination.
                </p>

                <h5>International Data Use</h5>

                <p>
                    Unified is headquartered in Canada. Your personal information may be collected, used and stored in the
                    United States or other locations outside of your home country, where data protection laws may differ
                    from those in your home country. Unless explicitly stated elsewhere in this Privacy Policy, by accessing
                    or using our Services, you agree that all information processed by us may be transferred, processed,
                    and stored anywhere in the world, including but not limited to, the United States. We endeavor to
                    safeguard your information consistent with the requirements of applicable laws. European Union users
                    should read the important information provided below on this page under
                    <a href="#eu-users">"Information for European Union Users"</a> about transfer of personal information
                    outside of the European Economic Area.
                </p>

                <h5>Information Security</h5>

                <p>
                    Unified takes reasonable steps to protect information we collect from you to prevent loss, misuse and
                    unauthorized access, disclosure, alteration, and destruction.
                </p>

                <p>
                    We follow generally accepted standards to protect the personal information submitted to us, both during
                    transmission and once we receive it. While we take reasonable efforts to guard personal information, it
                    is impossible to guarantee the security of information and systems. Therefore, we cannot ensure the
                    security of any information you transmit to us, and you do so at your own risk. You are also
                    responsible for taking reasonable steps to protect your personal information against unauthorized
                    disclosure or misuse, such as by using strong passwords.
                </p>

                <p>
                    Visit our <a target="_blank" href="https://app.mycroft.io/trust/unified-to">Trust Center</a> for more
                    information on how we protect your personal information.
                </p>

                <h5>Retention of Information</h5>

                <p>
                    We keep your personal information for as long as reasonably necessary to fulfill the purposes described
                    in this Privacy Policy, or any other notice provided at the time of collection, taking into account
                    applicable statutes of limitation and records retention requirements under applicable law. We may keep
                    personal information for longer periods where there is a reasonable basis for keeping such data,
                    including without limitation in connection with the establishment, exercise, or defense of legal
                    claims.
                </p>

                <h5>AI and Automated Decision-Making</h5>

                <p>
                    We use AI tools to analyze business contact and company information to identify and prioritize
                    prospective customers for our sales and marketing efforts. We do not use artificial intelligence or
                    automated decision-making technology to make significant decisions about individuals, as such terms are
                    defined under applicable law. When using a feature supported by AI, such Third Parties may record,
                    analyze, or process information in real time on our behalf.
                </p>

                <h5>Children's Information</h5>

                <p>
                    Our Services are not directed at individuals under the age of 18, and we do not knowingly collect
                    personal information directly from individuals under the age of 18. If you are the parent or guardian of
                    a child under the age of 18 and you believe that your child has provided us with personal information,
                    please contact us by emailing <a href="mailto:privacy@unified.to">privacy@unified.to</a>.
                </p>

                <h5>Updates to this Privacy Policy</h5>

                <p>
                    We may change or otherwise revise this Privacy Policy from time to time in our sole discretion. If we
                    make any changes to this Privacy Policy, we will change the date first written above. Except as may be
                    otherwise required under applicable law, revisions to this Privacy Policy will be effective on the date
                    noted above without any other notice or approval by you. If required under applicable law, such as in
                    the event of any material changes to this Privacy Policy, we will take additional steps as necessary to
                    notify you of such changes, such as by email (sent to the email address specified in your account) or a
                    notice on the Website once you log back in, and such revisions will not be effective until we have met
                    our obligations accordingly. We encourage you to check this Privacy Policy whenever you use our Services
                    to understand how your personal information is used.
                </p>

                <h5 id="eu-users">Information for European Union Users</h5>

                <p>
                    References to "personal information" in this Privacy Policy are equivalent to "personal data" governed
                    by European data protection legislation.
                </p>

                <p>
                    <i>Controller</i> — Except with respect to Customer Services Data, Unified is the controller of your
                    personal information covered by this Privacy Policy for purposes of European data protection
                    legislation. Contact us at privacy@unified.to. With respect to Customer Services Data, Unified is a
                    processor or sub-processor for purposes of European data protection legislation.
                </p>

                <p>
                    <i>EU Representative</i> — Unified has appointed an EU representative. You may contact our EU
                    representative at: <a href="mailto:eu-rep@unified.to">eu-rep@unified.to</a>
                </p>

                <p>
                    <i>Retention</i> — We will retain your personal information for as long as necessary to fulfill the
                    purposes we collected it for, unless you request deletion of such data or such data is no longer
                    relevant. In some cases, we may have to retain data to comply with legal obligations, including, but not
                    limited to accounting or reporting requirements.
                </p>

                <p>
                    <i>Your rights</i> — You may ask us to provide you with information about our processing of your
                    personal information, opt-out of marketing communication, correct or delete certain personal
                    information, transfer it to you or a third-party of your choice, restrict further processing of your
                    personal information, or object to our need to process that information. Please send these requests to
                    privacy@unified.to. We may accept or decline your request, subject to applicable law and legal
                    restrictions. If you would like to file a complaint, you may contact us or submit one to your
                    jurisdiction's data protection regulator found
                    <a target="_blank"
                        href="https://ec.europa.eu/justice/article-29/structure/data-protection-authorities/index_en.htm">here</a>.
                </p>

                <p>
                    <i>Transfers of Personal Data</i> — Unified transfers personal data between jurisdictions in accordance
                    with applicable data protection laws. Where required, we rely on adequacy decisions or the European
                    Commission's Standard Contractual Clauses (SCCs). Module Two or Module Three applies as appropriate. The
                    optional docking clause (Clause 7) is included. The optional independent dispute resolution body under
                    Clause 11(a) is not selected. Option 2 under Clause 9(a) applies. Where required, the applicable UK
                    international data transfer addendum applies.
                </p>

                <p>
                    We protect your customers' data.
                    <a target="_blank" href="https://app.mycroft.io/trust/unified-to">Visit our Trust Center</a>.
                </p>

                <h5>Contact Us</h5>

                <p>
                    If you have any questions, concerns, or would otherwise like to contact us regarding our privacy
                    practices, this Privacy Policy, any rights you may have hereunder, or if you wish to submit a request to
                    exercise your choices as detailed in this Privacy Notice, please email us at
                    <a href="mailto:privacy@unified.to">privacy@unified.to</a>.
                </p>

                <h5>Accessibility</h5>

                <p>
                    If you would like to receive this Privacy Policy in an alternative format to accommodate a disability,
                    please contact us at <a href="mailto:privacy@unified.to">privacy@unified.to</a>.
                </p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { setTitle } from '~~/services/common';

setTitle(
    'Privacy Policy',
    'Unified.to Privacy Policy explaining how we collect, use, and protect personal information.'
);
</script>

<style scoped>
h5 {
    margin-bottom: 20px;
    margin-top: 20px;
}

p {
    margin-bottom: 10px;
    color: var(--medium-gray);
}

ul {
    padding-left: 40px;
    margin-top: 20px;
    list-style: disc;
    color: var(--medium-gray);
}

ul li {
    margin-bottom: 10px;
}

a {
    text-decoration: underline;
}
</style>